import City from "./City";
import Note from './Note';
import Media from './Media';
import Biometric from './Biometric';
import Gender from '../enums/Gender';
import Appointment from "./Appointment";
import { asset, __ } from '../support/helpers';
import Practitioner from "./Practitioner";
import Prescription from "./Prescription";
import {ClassTransformOptions, Exclude, plainToClass, Type} from "class-transformer";
import Treatment from './Treatment';
import Invoice from './Invoice';
import Tooth from './Tooth';

declare var window: any;

class Patient {
    id: number;
    firstname: string;
    lastname: string;
    cin: string;
    email: string;
    gender: string;
    mobile: string;
    home: string;
    office: string;
    dial_code: string;
    address: string;
    postal_code: string;
    city_id: number;
    creator: string;

    @Type(() => Date)
    birthdate: Date;

    @Type(() => Date)
    verified_at: Date;

    @Type(() => Date)
    created_at: Date;

    @Type(() => Date)
    updated_at: Date;

    @Exclude()
    pivot: object;

    @Exclude()
    $option: HTMLOptionElement;

    /*
    |--------------------------------------------------------------------------
    | Relationships
    |--------------------------------------------------------------------------
    */

    @Type(() => Media)
    avatar: Media;

    @Type(() => City)
    city: City;

    @Type(() => Appointment)
    appointments: Appointment[];

    @Type(() => Biometric)
    biometrics: Biometric[];

    @Type(() => Appointment)
    before_last_appointment: Appointment;

    @Type(() => Practitioner)
    practitioners: Practitioner[];

    @Type(() => Prescription)
    prescriptions: Prescription[];

    @Type(() => Note)
    notes: Note[];

    @Type(() => Media)
    documents: Media[];

    @Type(() => Treatment)
    treatments: Treatment[];

    @Type(() => Invoice)
    invoices: Invoice[];

    /*
    |--------------------------------------------------------------------------
    | Methods
    |--------------------------------------------------------------------------
    */

    /**
     * Get full name
     *
     * @return {string}
     */
    fullName() {
        return `${this.firstname} ${this.lastname}`;
    }

    /**
     * Get practitioner's profile image
     *
     * @return string
     */
    avatarUrl(conversionName?: string): string {
        return this.hasAvatar()
            ? this.avatar.getUrl(conversionName)
            : asset('images/avatar_' + (this.hasGender() ? this.gender : 'm') + '.svg');
    }

    /**
     * Check if practitioner has a profile image.
     *
     * @return boolean
     */
    hasAvatar(): boolean {
        return !!this.avatar;
    }

    /**
     * Get birthday
     *
     * @param {string} format
     * @return {string}
     */
    getBirthday(format = 'YYYY-MM-DD') {
        return this.hasBirthdate() ? window.moment(this.birthdate).format(format) : null;
    }

    /**
     * Get patient age
     *
     * @return {number|null}
     */
    age() {
        return this.hasBirthdate() ? window.moment().diff(this.birthdate, 'years', false) : null;
    }

    /**
     * Check if patient is male
     *
     * @return boolean
     */
    isMale(): boolean {
        return this.gender === Gender.MALE;
    }

    /**
     * Get long form of Mr/Mrs
     *
     * @return {string}
     */
   longTitle(): string {
        return __(`long_title_${this.gender}`);
    }

    /**
     * Check if patient has specified his gender
     *
     * @returns {boolean}
     */
    hasGender(): boolean {
        return !!this.gender;
    }

    /**
     * Check if birthdate is set
     *
     * @return {boolean}
     */
    hasBirthdate(): boolean {
        return this.birthdateIsValid();
    }

    /**
     * Check if birthdate is valid
     * @see https://stackoverflow.com/a/1353711/4325011
     *
     * @returns {boolean}
     */
    birthdateIsValid(): boolean {
        // @ts-ignore
        return this.birthdate instanceof Date && !isNaN(this.birthdate);
    }

    /**
     * Get gender
     *
     * @return {string}
     */
    getGender() {
        // return Lang.strings(`gender_${this.gender}`);
    }

    /**
     *  Get city name
     *
     * @param {string} [locale]
     * @return {string|null}
     */
    cityName(locale?: string) {
        return this.city?.name(locale);
    }

    /**
     * Get unbilled treatments.
     *
     * @returns {Treatment[]}
     */
    unbilledTreatments(): Treatment[] {
        return this.treatments.filter(treatment => !treatment.isBilled());
    }

    /**
     * Check if patient has valid email.
     *
     * @returns {boolean}
     */
    hasValidEmail(): boolean {
        return this.hasEmail() && this.emailIsNotAutoGenerated();
    }

    /**
     * Check if patient email is not auto-generated.
     *
     * @returns {boolean}
     */
    emailIsNotAutoGenerated(): boolean {
        return !this.emailIsAutoGenerated();
    }

    /**
     * Check if patient email is auto-generated.
     *
     * @returns {boolean}
     */
    emailIsAutoGenerated(): boolean {
        return this.email?.startsWith('doctori-');
    }

    /**
     * Check if patient has email.
     *
     * @returns {boolean}
     */
    hasEmail(): boolean {
        return !!this.email;
    }

    restoredTeeth(): Tooth[] {
        return this.treatments
        // get treated teeth
        .reduce((acc: Tooth[], treatment) => {
            // The concat() method is used to merge arrays.
            // Concat does not change the existing arrays, but instead returns a new array.
            return acc.concat(treatment.teeth);
        }, [])
        // make sure there are no duplicates
        .filter((tooth: Tooth, index, self) => {
            return self.findIndex(t => t.number === tooth.number) === index;
        });
    }

    /**
     * alias
     *
     * @return {Appointment}
     */
    get beforeLastAppointment() {
        return this.before_last_appointment;
    }

    /**
     * Converts plain (literal) object to class (constructor) object. Also works with arrays.
     */
    static new(plain: [], options?: ClassTransformOptions): Patient[];
    static new(plain: {}, options?: ClassTransformOptions): Patient;
    static new(plain: {} | [], options?: ClassTransformOptions): Patient | Patient[] {
        return plainToClass(Patient, plain as any, options);
    }
}

export default Patient;
